// NEW_FILE_JUSTIFIED: Step 5 of claim wizard — review summary + submit, extracted to stay under 200 lines
import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Loader2, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';
import type { IClaimFormState } from '@/hooks/useClaimForm';

// LOCAL_TYPE_JUSTIFIED: step-internal props, not reused outside wizard
interface IClaimWizardStepReviewProps {
  orgId: Id<'organisations'>;
  state: IClaimFormState;
  onPrev: () => void;
  onSuccess: (claimId: Id<'claims'>) => void;
}

function ReviewRow({ label, value }: { label: string; value: string | undefined }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-40 shrink-0 text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export function ClaimWizardStepReview({
  orgId,
  state,
  onPrev,
  onSuccess,
}: IClaimWizardStepReviewProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createClaim = useMutation(api.insurance.claims.create);
  const addLineItem = useMutation(api.insurance.claims.addLineItem);
  const uploadDoc = useMutation(api.insurance.claimDocuments.upload);

  const totalClaimed = state.lineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);
  const currency = state.lineItems[0]?.currency ?? 'EUR';

  const handleSubmit = useCallback(async () => {
    if (!state.policyId || !state.dateOfService) {
      toast.error('Missing required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      const claimId = await createClaim({
        orgId,
        policyId: state.policyId as Id<'insurancePolicies'>, // AS_JUSTIFIED: policyId selected from active policy list, safe to narrow to typed Id
        claimantType: 'employee',
        claimantId: 'self',
        treatmentType: state.treatmentType || undefined,
        dateOfService: new Date(state.dateOfService).getTime(),
        dateOfInjuryOrIllness: state.dateOfInjuryOrIllness
          ? new Date(state.dateOfInjuryOrIllness).getTime()
          : undefined,
        diagnosis: state.diagnosis || undefined,
        symptoms: state.symptoms || undefined,
        providerName: state.providerName || undefined,
        providerAddress: state.providerAddress || undefined,
        providerPhone: state.providerPhone || undefined,
        providerEmail: state.providerEmail || undefined,
        totalAmountClaimed: totalClaimed,
        currency,
        bankName: state.bankName || undefined,
        bankAccountHolder: state.bankAccountHolder || undefined,
        bankIban: state.bankIban || undefined,
        bankSwift: state.bankSwift || undefined,
        reimburseTarget:
          state.reimburseTarget === 'insured' || state.reimburseTarget === 'provider'
            ? state.reimburseTarget
            : undefined,
        customFieldValues:
          Object.keys(state.customFieldValues).length > 0 ? state.customFieldValues : undefined,
      });

      await Promise.all(
        state.lineItems
          .filter(li => li.description && li.amount)
          .map(li =>
            addLineItem({
              claimId,
              dateOfService: li.dateOfService
                ? new Date(li.dateOfService).getTime()
                : new Date(state.dateOfService).getTime(),
              description: li.description,
              amountClaimed: parseFloat(li.amount),
              currency: li.currency,
            })
          )
      );

      await Promise.all(
        state.uploadedDocs.map(doc =>
          uploadDoc({
            claimId,
            storageId: doc.storageId as Id<'_storage'>, // AS_JUSTIFIED: storageId from Convex upload response, safe to narrow to typed Id
            fileName: doc.fileName,
            fileSize: doc.fileSize,
            mimeType: doc.mimeType,
            documentType: doc.documentType as
              | 'prescription'
              | 'receipt'
              | 'invoice'
              | 'referral'
              | 'other', // AS_JUSTIFIED: documentType is controlled via upload step, values constrained to literal union
          })
        )
      );

      toast.success('Claim submitted successfully!');
      onSuccess(claimId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [state, orgId, totalClaimed, currency, createClaim, addLineItem, uploadDoc, onSuccess]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">Review your claim details before submitting.</p>
      <div className="space-y-2">
        <p className="text-sm font-semibold">Claim Details</p>
        <Separator />
        <ReviewRow label="Treatment Type" value={state.treatmentType} />
        <ReviewRow label="Date of Service" value={state.dateOfService} />
        <ReviewRow label="Diagnosis" value={state.diagnosis} />
        <ReviewRow label="Provider" value={state.providerName} />
      </div>
      {state.lineItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold">Line Items</p>
          <Separator />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Currency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {state.lineItems.map(li => (
                <TableRow key={li.tempId}>
                  <TableCell>{li.description}</TableCell>
                  <TableCell className="text-right">{li.amount}</TableCell>
                  <TableCell>{li.currency}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-right text-sm font-semibold">
            Total: {totalClaimed.toFixed(2)} {currency}
          </p>
        </div>
      )}
      {state.uploadedDocs.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold">Documents</p>
          <Separator />
          {state.uploadedDocs.map(doc => (
            <div key={doc.storageId} className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {doc.fileName}
            </div>
          ))}
        </div>
      )}
      {state.bankIban && (
        <div className="space-y-2">
          <p className="text-sm font-semibold">Payment</p>
          <Separator />
          <ReviewRow label="Bank" value={state.bankName} />
          <ReviewRow label="IBAN" value={state.bankIban} />
          <ReviewRow label="Reimburse to" value={state.reimburseTarget} />
        </div>
      )}
      <div className="flex justify-between pt-2">
        <Button variant="outline" onClick={onPrev} disabled={isSubmitting}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
          data-testid="claim-wizard-submit"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" /> Submit Claim
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// LOCAL_TYPE_JUSTIFIED: success panel props, used only in this wizard file
interface IClaimSuccessProps {
  claimId: Id<'claims'>;
  basePath: string;
  onReset: () => void;
}

export function ClaimSuccessPanel({ claimId, basePath, onReset }: IClaimSuccessProps) {
  return (
    <div className="flex flex-col items-center gap-4 py-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
        <Check className="h-7 w-7 text-primary" />
      </div>
      <div>
        <p className="text-lg font-semibold">Claim Submitted!</p>
        <p className="text-sm text-muted-foreground">Your claim is now under review.</p>
      </div>
      <div className="flex gap-3">
        <Button asChild>
          <a href={`${basePath}/claims/${claimId}`}>View Claim</a>
        </Button>
        <Button variant="outline" onClick={onReset}>
          Submit Another
        </Button>
      </div>
    </div>
  );
}

// Export Badge so it can be used in the parent for the step indicator without extra import
export { Badge };
